import { useEffect, useState } from "react";
import { api, euro } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const PALETTE = ["#00396a", "#1d6fb8", "#4aa3df", "#8fc7ec", "#0b8457", "#e6a700", "#c0392b", "#7d3c98"];
const GRID = "#E4E4E7";

function ChartCard({ title, children, className = "" }) {
  return (
    <Card className={`rounded-sm border-border shadow-none p-6 ${className}`}>
      <h3 className="font-heading text-base font-semibold mb-4">{title}</h3>
      {children}
    </Card>
  );
}

const tip = { contentStyle: { background: "#09090B", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 } };

export default function Statistics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then(({ data }) => setD(data)); }, []);

  if (!d) return <InternalLayout><div className="p-12 grid grid-cols-2 gap-6">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-64 rounded-sm" />)}</div></InternalLayout>;

  const totals = d.totals;
  const TotalCard = ({ label, value }) => (
    <Card className="rounded-sm border-border shadow-none p-5">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="font-heading text-2xl font-bold mt-2 tabular-nums">{value}</div>
    </Card>
  );

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader title="Statistiche" subtitle="Analisi incrociata su clienti, contratti, ticket, riparazioni e servizi" />

        <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
          <TotalCard label="Clienti" value={totals.clienti} />
          <TotalCard label="Contratti" value={totals.contratti} />
          <TotalCard label="Dispositivi" value={totals.dispositivi} />
          <TotalCard label="Ticket" value={totals.ticket} />
          <TotalCard label="Riparazioni" value={totals.riparazioni} />
          <TotalCard label="Servizi" value={totals.servizi} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ChartCard title="Ricavi ricorrenti mensili per servizio">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.mrr_by_service}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => euro(v)} {...tip} />
                <Bar dataKey="value" fill="#00396a" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Top clienti per ricavo ricorrente">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.top_clients} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => euro(v)} {...tip} />
                <Bar dataKey="value" fill="#1d6fb8" radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Ticket per stato">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={d.tickets_by_stato} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {d.tickets_by_stato.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Ticket per tecnico">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.tickets_by_tecnico}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip {...tip} />
                <Bar dataKey="value" fill="#0b8457" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Andamento ticket nel tempo">
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={d.tickets_trend}>
                <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00396a" stopOpacity={0.4} /><stop offset="95%" stopColor="#00396a" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip {...tip} />
                <Area type="monotone" dataKey="value" stroke="#00396a" strokeWidth={2} fill="url(#g1)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Riparazioni per stato">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.repairs_by_stato}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip {...tip} />
                <Bar dataKey="value" fill="#4aa3df" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Esito riparazioni">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={d.repairs_outcome} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} label>
                  <Cell fill="#0b8457" /><Cell fill="#c0392b" />
                </Pie>
                <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Ricavi riparazioni nel tempo">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={d.repairs_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => euro(v)} {...tip} />
                <Line type="monotone" dataKey="value" stroke="#00396a" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Margine servizi per tipologia">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.services_margin_by_tipo}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => euro(v)} {...tip} />
                <Bar dataKey="value" fill="#e6a700" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Scadenze servizi per mese">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.services_expiring_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip {...tip} />
                <Bar dataKey="value" fill="#c0392b" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Servizi per fornitore">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={d.services_by_fornitore} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {d.services_by_fornitore.map((e, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip {...tip} /><Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Contratti in scadenza per mese">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={d.contracts_expiring_by_month}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip {...tip} />
                <Bar dataKey="value" fill="#7d3c98" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </InternalLayout>
  );
}
